import { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../axiosInstance';

const EMAILS_BASE = '/emails';

/* Category → human label. Anything unmapped falls back to a tidied version of the
   raw value rather than showing a bare slug. */
const CATEGORY_LABELS = {
  receipt: 'Payment Receipt',
  invoice: 'Invoice',
  voucher: 'Voucher',
  confirmation: 'Booking Confirmation',
  password_reset: 'Password Reset',
  notification: 'Notification',
  other: 'Email',
};

export const getCategoryLabel = (category) =>
  CATEGORY_LABELS[category] ||
  String(category || 'Email')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

/* Status → what the operator actually needs to understand.
   SKIPPED is its own state on purpose: "we never tried" is a different problem
   from "the provider rejected it", and it means the API key is missing. */
export const STATUS_META = {
  SENT: { label: 'Delivered', tone: 'sent' },
  FAILED: { label: 'Failed', tone: 'failed' },
  SKIPPED: { label: 'Not sent', tone: 'skipped' },
};

export const mapEmailToUi = (e) => ({
  id: e.id,
  category: e.category,
  categoryLabel: getCategoryLabel(e.category),
  status: e.status,
  statusLabel: STATUS_META[e.status]?.label || e.status,
  tone: STATUS_META[e.status]?.tone || 'skipped',
  bookingReference: e.bookingReference || null,
  documentId: e.documentId ?? null,
  toEmail: e.toEmail,
  toName: e.toName || null,
  fromEmail: e.fromEmail,
  subject: e.subject,
  attachments: Array.isArray(e.attachments) ? e.attachments : [],
  messageId: e.messageId || null,
  error: e.error || null,
  sentAt: e.sentAt || e.createdAt || null,
  _raw: e,
});

/**
 * Email delivery log for one booking.
 *
 * `meta` carries the server-side tallies (total/sent/failed/skipped) plus a
 * `truncated` flag, so the summary strip reports the real totals instead of
 * counting a possibly-capped page.
 */
export const useGetBookingEmails = (bookingReference) => {
  const [data, setData] = useState([]);
  const [meta, setMeta] = useState({ total: 0, sent: 0, failed: 0, skipped: 0, truncated: false });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // `silent` refreshes without flashing the skeleton or clobbering the list on a
  // transient failure — used by the manual refresh button.
  const fetchEmails = useCallback(
    async ({ silent = false } = {}) => {
      if (!bookingReference) return false;
      if (!silent) {
        setLoading(true);
        setError(null);
      }
      try {
        const res = await axiosInstance.get(
          `${EMAILS_BASE}/booking/${encodeURIComponent(bookingReference)}`
        );
        const rows = (res.data?.data || []).map(mapEmailToUi);
        setData(rows);
        setMeta({
          total: res.data?.meta?.total ?? rows.length,
          sent: res.data?.meta?.sent ?? 0,
          failed: res.data?.meta?.failed ?? 0,
          skipped: res.data?.meta?.skipped ?? 0,
          truncated: Boolean(res.data?.meta?.truncated),
        });
        return true;
      } catch (err) {
        if (!silent) {
          setError(err.response?.data?.message || 'Failed to load the email log');
        }
        return false;
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [bookingReference]
  );

  useEffect(() => {
    fetchEmails();
  }, [fetchEmails]);

  return {
    data,
    meta,
    loading,
    error,
    refetch: fetchEmails,
    refetchSilent: () => fetchEmails({ silent: true }),
  };
};
